import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { ArrowRight, Scale } from 'lucide-react';
import { Modal } from '../../../../../components/ui/Modal';
import { Button } from '../../../../../components/ui/Button';
import { Switch } from '../../../../../components/ui/Switch';
import { FilterableSelect } from '../../../../../components/ui/FilterableSelect';
import { ConfirmDialog } from '../../../../../components/ui/ConfirmDialog';
import { ErrorState } from '../../../../../components/ui/ErrorState';
import { ShopifyRunSummary } from '../../../../../components/warehouses/ShopifyRunSummary';
import { ShopifyScopesNotice } from '../../../../../components/warehouses/ShopifyScopesNotice';
import { notify } from '../../../../../features/feedback/notify';
import { selectIsRunning } from '../../../../../features/feedback/feedbackSlice';
import { stockSyncChannelSelected } from '../../../../../features/stockSync/stockSyncSlice';
import {
  fetchStockSync,
  fetchStoreLocations,
  startStockPreview,
  updateStockSync,
} from '../../../../../features/stockSync/stockSyncThunks';
import {
  selectChannelOptions,
  selectIsRunActive,
  selectLocationOptions,
  selectRunOf,
  selectRunPreviewState,
} from '../../../../../features/stockSync/stockSyncSelectors';
import {
  RUN_KIND,
  SHOPIFY_ALIGNMENT_PATH,
  SHOPIFY_LISTINGS_PATH,
  STOCK_SYNC_NOTICE,
} from '../../../../../features/stockSync/stockSyncConstants';
import {
  PREVIEW_STATE,
  alignmentScopesProblemOf,
  canSyncStock,
  formatSyncDate,
  formatSyncTime,
  notLoadedText,
  productCount,
  publishBlocker,
  publishConfirmMessage,
  publishedCount,
  publishSummary,
  scopesProblemOf,
  stockCompareBlocker,
} from '../../../../../features/stockSync/stockSyncUtils';
import { formatQuantity } from '../../../../../utils/inventoryLabels';

const SECTION = 'space-y-3 border-t border-border pt-5';
const FIELD_ERROR = 'text-sm font-semibold text-danger';

/** What a refusal says about one setting: its field's sentence, or the whole one. */
const fieldErrorOf = (payload, field) => payload?.fieldErrors?.[field] || payload?.message || null;

/** Under the last comparison: until when it is good and where it is reviewed, or why it no longer is. */
const PreviewFooter = ({ state, report, settingId }) => {
  if (state === PREVIEW_STATE.READY) {
    return (
      <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
        <span className="text-text-muted" data-testid="shopify-stock-expiry" data-state={state}>
          Vence a las {formatSyncTime(report.result?.expires_at)}
        </span>
        <Link
          to={`${SHOPIFY_ALIGNMENT_PATH}?store=${settingId}`}
          data-testid="shopify-stock-review-link"
          className="inline-flex items-center gap-1.5 font-semibold text-ninja-green hover:underline"
        >
          Revisar y aplicar
          <ArrowRight size={16} aria-hidden="true" />
        </Link>
      </div>
    );
  }
  if (state === PREVIEW_STATE.EXPIRED) {
    return (
      <p className="text-text-muted" data-testid="shopify-stock-expiry" data-state={state}>
        Venció a las {formatSyncTime(report.result?.expires_at)}: compara de nuevo para aplicar.
      </p>
    );
  }
  if (state === PREVIEW_STATE.APPLIED) {
    return (
      <p className="text-text-muted" data-testid="shopify-stock-expiry" data-state={state}>
        Aplicada · {formatSyncDate(report.applied_at)}
      </p>
    );
  }
  return null;
};

/**
 * A Shopify store's stock with this warehouse: which of the store's locations
 * is the warehouse, aligning Ninja's stock with the store's (compared first,
 * reviewed and applied on a page of its own), and publishing Ninja's
 * available stock there from then on.
 *
 * Publishing asks first. A comparison goes on in the server: the page keeps
 * watching it (`stockSyncListeners`).
 *
 * @param {object} props
 * @param {() => void} props.onClose
 */
const ShopifyInventoryDialog = ({ onClose }) => {
  const dispatch = useDispatch();
  const channelOptions = useSelector(selectChannelOptions);
  const { selectedId, detail, detailLoading, detailError, locationsLoading, locationsError } = useSelector(
    (state) => state.stockSync
  );
  const locationOptions = useSelector(selectLocationOptions);
  const previewReport = useSelector(selectRunOf(RUN_KIND.STOCK_PREVIEW));
  const previewState = useSelector(selectRunPreviewState);
  const previewRunning = useSelector(selectIsRunActive(RUN_KIND.STOCK_PREVIEW));
  const alignmentReport = useSelector(selectRunOf(RUN_KIND.STOCK_ALIGNMENT));
  const savingLocation = useSelector(selectIsRunning(updateStockSync, 'location_id'));
  const savingPublish = useSelector(selectIsRunning(updateStockSync, 'enabled'));
  const startingPreview = useSelector(selectIsRunning(startStockPreview));

  /** `{kind: 'publish'|'location', locationId?}` while a confirmation is open. */
  const [confirming, setConfirming] = useState(null);
  const [confirmError, setConfirmError] = useState(null);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!selectedId) return;
    setErrors({});
    dispatch(fetchStockSync({ settingId: selectedId }));
    dispatch(fetchStoreLocations({ settingId: selectedId }));
  }, [dispatch, selectedId]);

  /** Saves one setting; a refusal stays next to its control, or in the open confirmation. */
  const save = async (field, changes, success) => {
    setErrors((current) => ({ ...current, [field]: null }));
    const action = await dispatch(updateStockSync({ settingId: selectedId, changes }));
    if (updateStockSync.fulfilled.match(action)) {
      setConfirming(null);
      if (success) dispatch(notify.success(success, { testId: STOCK_SYNC_NOTICE }));
      return;
    }
    if (confirming) setConfirmError(fieldErrorOf(action.payload, field));
    else setErrors((current) => ({ ...current, [field]: fieldErrorOf(action.payload, field) }));
  };

  const openConfirm = (next) => {
    setConfirmError(null);
    setConfirming(next);
  };

  const handleLocation = (locationId) => {
    if (locationId === detail.location?.id) return;
    // While publishing, the stock goes out again to the new location.
    if (detail.enabled) openConfirm({ kind: 'location', locationId });
    else save('location_id', { location_id: locationId });
  };

  const handlePublish = (enabled) => {
    if (enabled) openConfirm({ kind: 'publish' });
    else save('enabled', { enabled: false }, 'Ninja dejó de publicar el stock en la tienda.');
  };

  // Comparing only reads: it asks nothing first.
  const handleCompare = async () => {
    setErrors((current) => ({ ...current, compare: null }));
    const action = await dispatch(startStockPreview({ settingId: selectedId }));
    if (startStockPreview.rejected.match(action)) setErrors((current) => ({ ...current, compare: action.payload }));
  };

  const locationName = detail?.location?.name;
  const confirmations = detail && {
    publish: {
      title: 'Publicar el stock de Ninja',
      message: publishConfirmMessage(detail),
      confirmLabel: 'Publicar',
      loadingLabel: 'Publicando...',
      loading: savingPublish,
      onConfirm: () =>
        save('enabled', { enabled: true }, `Ninja publica el stock en «${locationName}» de la tienda.`),
    },
    location: {
      title: 'Cambiar la ubicación de la tienda',
      message: `Ninja vuelve a publicar el stock de ${productCount(publishedCount(detail.listings))} en la ubicación nueva.`,
      confirmLabel: 'Cambiar',
      loadingLabel: 'Cambiando...',
      loading: savingLocation,
      onConfirm: () => save('location_id', { location_id: confirming?.locationId }),
    },
  };
  const confirmation = confirming && confirmations ? confirmations[confirming.kind] : null;

  const scopesProblem = detail && scopesProblemOf(detail);
  const syncs = canSyncStock(detail);
  const compareBlocker = detail && stockCompareBlocker(detail);
  const publishBlock = detail && publishBlocker(detail);

  return (
    <Modal isOpen onClose={onClose} title="Inventario en Shopify" size="lg" testId="shopify-inventory-modal">
      <div className="space-y-5">
        {channelOptions.length > 1 && (
          <FilterableSelect
            label="Tienda"
            variant="form"
            value={selectedId ?? ''}
            onChange={(settingId) => dispatch(stockSyncChannelSelected(Number(settingId)))}
            options={channelOptions}
            placeholder="Elige la tienda"
            searchPlaceholder="Buscar cliente o tienda..."
            testId="shopify-store"
          />
        )}

        {selectedId && detailLoading && !detail && (
          <div className="flex justify-center py-8">
            <div className="w-8 h-8 border-4 border-ninja-green border-t-transparent rounded-full animate-spin" />
          </div>
        )}
        {selectedId && detailError && (
          <ErrorState
            variant="inline"
            title="No se pudo leer la tienda"
            description={detailError}
            onRetry={() => dispatch(fetchStockSync({ settingId: selectedId }))}
          />
        )}

        {detail && (
          <>
            <p className="text-sm text-text-muted" data-testid="shopify-store-summary">
              {detail.channel.warehouse.name} · {formatQuantity(detail.listings?.total ?? 0)}{' '}
              {detail.listings?.total === 1 ? 'producto vinculado' : 'productos vinculados'}
            </p>
            <ShopifyScopesNotice problem={scopesProblem} companyName={detail.channel.company.name} />

            <div className="space-y-2">
              <FilterableSelect
                label="Ubicación en Shopify"
                variant="form"
                value={detail.location?.id ?? ''}
                onChange={handleLocation}
                options={locationOptions}
                placeholder={locationsLoading ? 'Leyendo las ubicaciones de la tienda…' : 'Elige la ubicación'}
                disabled={locationsLoading || savingLocation || Boolean(locationsError)}
                hasError={Boolean(errors.location_id)}
                testId="shopify-location"
              />
              <p className="text-xs text-text-muted">
                La ubicación de la tienda que es esta bodega: de ahí se trae el stock y ahí se publica.
              </p>
              {locationsError && (
                <ErrorState
                  variant="inline"
                  title="No se pudieron leer las ubicaciones de la tienda"
                  description={locationsError}
                  onRetry={() => dispatch(fetchStoreLocations({ settingId: selectedId }))}
                />
              )}
              {errors.location_id && <p className={FIELD_ERROR}>{errors.location_id}</p>}
            </div>

            {syncs && (
              <section className={SECTION} data-testid="shopify-stock-sync">
                <h3 className="font-bold text-ninja-dark dark:text-white">Sincronizar stock con Shopify</h3>
                <p className="text-sm text-text-secondary">
                  Ninja deja su disponible igual al de la tienda en{' '}
                  {locationName ? `«${locationName}»` : 'la ubicación de la tienda'}: recibe lo que le falta y saca lo
                  que le sobra, solo de lo libre. Antes te muestra qué va a hacer con cada producto. Con «Publicar»
                  encendido, también corre sola cada noche: aplica lo que la revisión marcaría y deja lo demás para
                  revisar.
                </p>
                {/* The sync's own notice already asks to authorize again. */}
                {!scopesProblem && (
                  <ShopifyScopesNotice
                    problem={alignmentScopesProblemOf(detail)}
                    companyName={detail.channel.company.name}
                    forComparison
                  />
                )}
                <p className="text-sm font-semibold text-ninja-dark dark:text-white" data-testid="shopify-not-loaded">
                  {notLoadedText(detail.listings)}
                </p>
                <ShopifyRunSummary
                  kind={RUN_KIND.STOCK_PREVIEW}
                  report={previewReport}
                  footer={<PreviewFooter state={previewState} report={previewReport} settingId={detail.channel.id} />}
                />
                <ShopifyRunSummary kind={RUN_KIND.STOCK_ALIGNMENT} report={alignmentReport} />
                <div className="flex justify-end">
                  <Button
                    variant="outline"
                    icon={Scale}
                    loading={previewRunning || startingPreview}
                    loadingLabel="Comparando…"
                    disabled={Boolean(compareBlocker)}
                    title={compareBlocker || undefined}
                    onClick={handleCompare}
                    data-testid="shopify-stock-compare"
                  >
                    {previewState === PREVIEW_STATE.FAILED ? 'Reintentar' : 'Comparar con la tienda'}
                  </Button>
                </div>
                {compareBlocker && !previewRunning && (
                  <p className="text-right text-xs text-text-muted" data-testid="shopify-stock-compare-blocker">
                    {compareBlocker}
                  </p>
                )}
                {errors.compare && <p className={`${FIELD_ERROR} text-right`}>{errors.compare}</p>}
              </section>
            )}

            <section className={SECTION} data-testid="shopify-publishing">
              <div className="flex items-center justify-between gap-3">
                <h3 className="font-bold text-ninja-dark dark:text-white">Publicar el stock de Ninja en la tienda</h3>
                <Switch
                  checked={Boolean(detail.enabled)}
                  onChange={handlePublish}
                  label="Publicar"
                  loading={savingPublish}
                  disabled={Boolean(publishBlock)}
                  testId="shopify-publish"
                />
              </div>
              <p className="text-sm text-text-secondary">
                Ninja pone su disponible en {locationName ? `«${locationName}»` : 'la ubicación de la tienda'} y lo
                actualiza con cada venta, recepción o ajuste. Un ajuste hecho a mano en Shopify entra a Ninja como ajuste.
              </p>
              {publishBlock && (
                <p className="text-xs text-text-muted" data-testid="shopify-publish-blocker">
                  {publishBlock}
                </p>
              )}
              {errors.enabled && <p className={FIELD_ERROR}>{errors.enabled}</p>}
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="text-sm text-ninja-dark dark:text-white" data-testid="shopify-publish-summary">
                  {publishSummary(detail.listings)}
                </p>
                <Link
                  to={`${SHOPIFY_LISTINGS_PATH}?store=${detail.channel.id}`}
                  data-testid="shopify-listings-link"
                  className="inline-flex items-center gap-1.5 text-sm font-semibold text-ninja-green hover:underline"
                >
                  Ver estado por producto
                  <ArrowRight size={16} aria-hidden="true" />
                </Link>
              </div>
            </section>
          </>
        )}
      </div>

      <div className="flex justify-end gap-3 mt-8">
        <Button variant="ghost" onClick={onClose}>
          Cerrar
        </Button>
      </div>

      <ConfirmDialog
        isOpen={Boolean(confirmation)}
        onClose={() => setConfirming(null)}
        onConfirm={confirmation?.onConfirm}
        title={confirmation?.title ?? ''}
        message={confirmation?.message ?? ''}
        variant="primary"
        confirmLabel={confirmation?.confirmLabel}
        loadingLabel={confirmation?.loadingLabel}
        loading={Boolean(confirmation?.loading)}
        error={confirmError}
        testId="shopify-confirm"
      />
    </Modal>
  );
};

/** Mounted only while open: each opening reads the store, and its locations, again. */
export const ShopifyInventoryModal = ({ isOpen, ...props }) =>
  isOpen ? <ShopifyInventoryDialog {...props} /> : null;
